import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import PDFHeader from "./PDFHeader";
import { formatAmountInWords } from "../utils/numberToWords";
import { capitalizeWords } from "../utils/capitalizeWords";
import { getTenantNameUpper } from "../utils/tenantName";

Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Regular.ttf",
});

Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Bold.ttf",
  fontWeight: "bold",
});

const BORDER_COLOR = "#8c1d1a";
const BORDER = `1pt solid ${BORDER_COLOR}`;
const HEADER_BG = "#f5e9e9";
// A5 landscape — 8mm gap between page edge and content border (all sides)
const PAGE_MARGIN = 22.68; // 8mm in pt (8 * 72 / 25.4)
const A5_WIDTH = 595.28;
const A5_HEIGHT = 419.53;
const CONTENT_HEIGHT = A5_HEIGHT - PAGE_MARGIN * 2;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) {
      // Already formatted DD/MM/YYYY
      if (String(dateStr).includes("/")) return String(dateStr);
      return String(dateStr);
    }
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return String(dateStr);
  }
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "0.00";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** Absolute amount with Dr (due) / Cr (credit) — no minus sign. Zero stays "0.00". */
const formatAmountDrCr = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return "0.00";
  const tag = num > 0 ? "Dr" : "Cr";
  return `${formatAmount(Math.abs(num))} ${tag}`;
};

const cleanText = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

/**
 * Build voucher payload from form + API response.
 */
export function buildJamaVoucherData({
  isIn = true,
  party = {},
  amount,
  balance = null,
  date,
  remarks = "",
  paymentModeLabel = "Cash",
  apiResponse = null,
} = {}) {
  const data = apiResponse?.data ?? apiResponse ?? {};
  const amountNum = Number(amount) || 0;
  const mode = cleanText(paymentModeLabel) || "Cash";
  const paymentLabel = isIn
    ? `Credit - By ${mode}`
    : `Debit - Payment by ${mode}`;

  const voucherNo =
    data.voucher_number ??
    data.voucher_no ??
    data.doc_number ??
    data.cashbook_id ??
    data.id ??
    "-";

  const partyName =
    cleanText(party.name) ||
    cleanText(data.party_name) ||
    cleanText(data.customer_name) ||
    "";

  // Prefer explicit balance (pre-settlement due); fall back to API fields
  const balanceRaw =
    balance != null && balance !== ""
      ? balance
      : (data.total_due_amount ??
        data.balance ??
        data.due_amount ??
        data.remaining_amount ??
        0);
  const balanceNum = Number(balanceRaw);
  const balanceSafe = Number.isFinite(balanceNum) ? balanceNum : 0;
  const outstanding = balanceSafe - amountNum;

  return {
    title: isIn ? "Credit Voucher" : "Debit Voucher",
    voucherNo:
      voucherNo === null || voucherNo === undefined ? "-" : String(voucherNo),
    voucherDate: date || data.date || "",
    partyName,
    partyMobile:
      cleanText(party.mobile) ||
      cleanText(data.party_mobile) ||
      cleanText(data.mobile) ||
      "",
    partyAddress:
      cleanText(party.address) ||
      cleanText(data.address) ||
      cleanText(data.party_address) ||
      "",
    partyCity: cleanText(party.city) || cleanText(data.city) || "",
    partyGst:
      cleanText(party.gst) ||
      cleanText(party.gst_number) ||
      cleanText(data.gst_number) ||
      cleanText(data.GST_number) ||
      "",
    amount: amountNum,
    balance: balanceSafe,
    outstanding,
    amountInWords: formatAmountInWords(amountNum),
    paymentLabel,
    remarks: cleanText(remarks) || cleanText(data.remarks) || "",
  };
}

const styles = StyleSheet.create({
  page: {
    width: A5_WIDTH,
    height: A5_HEIGHT,
    padding: PAGE_MARGIN,
    fontFamily: "Helvetica",
    backgroundColor: "#fff",
    fontSize: 11,
    color: "#000",
    flexDirection: "column",
  },
  container: {
    height: CONTENT_HEIGHT,
    border: BORDER,
    borderRadius: 6,
    padding: 0,
    flexDirection: "column",
  },

  metaRow: {
    flexDirection: "row",
    borderBottom: BORDER,
    minHeight: 28,
  },
  metaCell: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  metaCellBorder: {
    borderRight: BORDER,
  },
  metaLabel: {
    fontSize: 11,
    fontFamily: "NotoSansGujaratiUI",
    fontWeight: "bold",
    color: "#000",
  },
  metaValue: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    marginLeft: 5,
    color: "#000",
  },

  sectionHeader: {
    backgroundColor: HEADER_BG,
    borderBottom: BORDER,
    paddingVertical: 7,
    paddingHorizontal: 10,
    alignItems: "center",
  },
  sectionHeaderText: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    color: BORDER_COLOR,
    textAlign: "center",
  },

  partyBlock: {
    borderBottom: BORDER,
    paddingTop: 6,
    paddingBottom: 4,
    paddingHorizontal: 12,
  },
  partyStack: {
    width: "100%",
  },
  fieldLine: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 5,
    width: "100%",
  },
  fieldLineLast: {
    marginBottom: 0,
  },
  // Wide enough for "PAN / GST"
  fieldLabelLeft: {
    width: 68,
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    textAlign: "left",
  },
  fieldColon: {
    width: 10,
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
  },
  fieldValue: {
    flex: 1,
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
  },

  // Transaction block — even gaps above Mr./Mrs and below Credit line
  bodyBlock: {
    paddingTop: 10,
    paddingBottom: 10,
    paddingHorizontal: 12,
  },
  // Mr./Mrs row — tight space name → underline
  bodyLineMr: {
    flexDirection: "row",
    alignItems: "flex-end",
    marginBottom: 10,
    width: "100%",
  },
  bodyLabelMr: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    marginRight: 6,
    paddingBottom: 3,
  },
  nameField: {
    flex: 1,
    borderBottom: `1pt solid ${BORDER_COLOR}`,
    paddingBottom: 3,
  },
  nameValue: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
  },
  bodyLine: {
    flexDirection: "row",
    alignItems: "flex-end",
    marginBottom: 8,
    width: "100%",
  },
  bodyLabel: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    marginRight: 6,
    paddingBottom: 3,
  },
  underlineAmount: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    borderBottom: `1pt solid ${BORDER_COLOR}`,
    paddingBottom: 3,
    paddingHorizontal: 5,
    minWidth: 90,
  },
  wordsInline: {
    flex: 1,
    fontSize: 10,
    fontFamily: "Helvetica",
    marginLeft: 8,
    paddingBottom: 3,
  },
  modeText: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    color: BORDER_COLOR,
    marginTop: 2,
    marginBottom: 0,
  },
  // Free height between body and note (page fill)
  midSpacer: {
    flex: 1,
    minHeight: 6,
  },
  // Full-width note — same padding style as body (gap above / below text)
  remarksBand: {
    width: "100%",
    alignSelf: "stretch",
    borderTop: BORDER,
    borderBottom: BORDER,
    paddingTop: 10,
    paddingBottom: 10,
    paddingHorizontal: 12,
    justifyContent: "center",
  },
  remarksLine: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
  },
  bodyColon: {
    width: 6,
    fontSize: 9,
    fontFamily: "Helvetica",
  },
  remarksLabel: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    color: "#333",
    marginRight: 2,
  },
  remarksText: {
    flex: 1,
    fontSize: 9,
    fontFamily: "Helvetica",
    color: "#222",
  },

  footer: {
    flexDirection: "row",
    minHeight: 72,
    // Note already has bottom border — no double line
    borderTop: "none",
  },
  totalBox: {
    width: "42%",
    borderRight: BORDER,
    flexDirection: "column",
    justifyContent: "center",
    // Even padding matching sign box so Balance | For, MANGLIK GOLD feel aligned
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  totalRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  totalRowLast: {
    marginBottom: 0,
  },
  totalRowLabel: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    color: "#333",
  },
  totalRowValue: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    color: "#000",
  },
  totalRowValueHighlight: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    color: BORDER_COLOR,
  },
  signBox: {
    flex: 1,
    alignItems: "flex-end",
    flexDirection: "column",
    justifyContent: "space-between",
    alignSelf: "stretch",
    paddingHorizontal: 12,
    paddingVertical: 10,
    paddingRight: 14,
  },
  signTop: {
    alignItems: "flex-end",
    justifyContent: "flex-start",
  },
  signFor: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
    marginBottom: 4,
  },
  signatureImage: {
    width: 100,
    height: 36,
    objectFit: "contain",
    flexShrink: 0,
  },
  signLabel: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
  },
});

const FieldLine = ({ label, value, last = false }) => (
  <View style={[styles.fieldLine, last && styles.fieldLineLast]}>
    <Text style={styles.fieldLabelLeft}>{label}</Text>
    <Text style={styles.fieldColon}>:</Text>
    <Text style={styles.fieldValue}>{value || " "}</Text>
  </View>
);

const JamaVoucherPDF = ({ voucherData }) => {
  const data = voucherData || {};
  const title = data.title || "Credit Voucher";
  const partyName = capitalizeWords(cleanText(data.partyName));
  const amountDisplay = formatAmount(data.amount);
  const balanceDisplay = formatAmountDrCr(data.balance);
  const outstandingRaw =
    data.outstanding != null && data.outstanding !== ""
      ? Number(data.outstanding)
      : (Number(data.balance) || 0) - (Number(data.amount) || 0);
  const outstandingDisplay = formatAmountDrCr(outstandingRaw);
  const amountInWords =
    cleanText(data.amountInWords) || formatAmountInWords(data.amount);
  const paymentLabel = cleanText(data.paymentLabel) || "Credit - Jama by Cash";
  const displayName = partyName ? partyName.toUpperCase() : " ";
  const companyNameUpper = getTenantNameUpper();

  return (
    <Document>
      {/* A5 landscape — identical paper setup to JangadPdfModal */}
      <Page size="A5" orientation="landscape" style={styles.page} wrap={false}>
        <View style={styles.container}>
          <PDFHeader
            title={title}
            titleStyle={{ fontSize: 13, paddingVertical: 1 }}
          />

          {/* Meta: date + voucher no */}
          <View style={styles.metaRow}>
            <View style={[styles.metaCell, styles.metaCellBorder]}>
              <Text style={styles.metaLabel}>Date :</Text>
              <Text style={styles.metaValue}>
                {formatDate(data.voucherDate)}
              </Text>
            </View>
            <View style={styles.metaCell}>
              <Text style={styles.metaLabel}>Voucher No :</Text>
              <Text style={styles.metaValue}>{data.voucherNo || "-"}</Text>
            </View>
          </View>

          {/* Receiver details */}
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>
              Details of Receiver | Billed to
            </Text>
          </View>
          <View style={styles.partyBlock}>
            <View style={styles.partyStack}>
              <FieldLine label="Name" value={partyName || "-"} />
              <FieldLine
                label="Address"
                value={
                  [cleanText(data.partyAddress), cleanText(data.partyCity)]
                    .filter(Boolean)
                    .join(", ") || "-"
                }
              />
              <FieldLine
                label="Mobile"
                value={cleanText(data.partyMobile) || "-"}
              />
              <FieldLine
                label="PAN / GST"
                last
                value={cleanText(data.partyGst) || "-"}
              />
            </View>
          </View>

          {/* Transaction lines */}
          <View style={styles.bodyBlock}>
            <View style={styles.bodyLineMr}>
              <Text style={styles.bodyLabelMr}>Mr./Mrs</Text>
              <View style={styles.nameField}>
                <Text style={styles.nameValue}>{displayName}</Text>
              </View>
            </View>

            <View style={styles.bodyLine}>
              <Text style={styles.bodyLabel}>Given</Text>
              <Text style={styles.underlineAmount}>{amountDisplay}</Text>
              <Text style={styles.wordsInline}>({amountInWords})</Text>
            </View>

            <Text style={styles.modeText}>{paymentLabel}</Text>
          </View>

          {/* Free space above note so note + footer sit together at the bottom */}
          <View style={styles.midSpacer} />

          {/* Note ends immediately above Balance / For, MANGLIK GOLD */}
          <View style={styles.remarksBand}>
            <View style={styles.remarksLine}>
              <Text style={styles.remarksLabel}>Note</Text>
              <Text style={styles.bodyColon}>:</Text>
              <Text style={styles.remarksText}>
                {cleanText(data.remarks) || " "}
              </Text>
            </View>
          </View>

          {/* Footer — Balance | signature, even padding both sides */}
          <View style={styles.footer}>
            <View style={styles.totalBox}>
              <View style={styles.totalRow}>
                <Text style={styles.totalRowLabel}>Balance</Text>
                <Text style={styles.totalRowValue}>{balanceDisplay}</Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={styles.totalRowLabel}>Amount</Text>
                <Text style={styles.totalRowValue}>{amountDisplay}</Text>
              </View>
              <View style={[styles.totalRow, styles.totalRowLast]}>
                <Text style={styles.totalRowLabel}>Outstanding</Text>
                <Text style={styles.totalRowValueHighlight}>
                  {outstandingDisplay}
                </Text>
              </View>
            </View>
            <View style={styles.signBox}>
              <View style={styles.signTop}>
                <Text style={styles.signFor}>For, {companyNameUpper}</Text>
                <Image
                  src="/assets/images/logos/signature.png"
                  style={styles.signatureImage}
                />
              </View>
              <Text style={styles.signLabel}>AUTHORISED SIGNATURE</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default JamaVoucherPDF;
